import React from 'react';
import { Plus, Trash2, PenLine, Check, Loader2, AlertCircle } from 'lucide-react';

const ROLE_OPTIONS = ['Supervisor', 'Terapeuta'];
const SPECIALTY_OPTIONS = ['Terapeuta Ocupacional', 'Fonoaudiólogo(a)', 'Psicólogo(a)'];

const TreatmentPlanSignoff = ({ signoffs = [], onSignoffsChange, readOnly = false, saveStatus = null }) => {
  const generateId = () => `sig-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const handleAdd = () => {
    onSignoffsChange([
      ...signoffs,
      { id: generateId(), role: 'Terapeuta', specialty: '', name: '', date: '', signed: false }
    ]);
  };

  const handleRemove = (id) => {
    onSignoffsChange(signoffs.filter((s) => s.id !== id));
  };

  const handleChange = (id, field, value) => {
    onSignoffsChange(
      signoffs.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  return (
    <>
      {/* Screen version */}
      <div className="signoff-section print:hidden mt-8 border-t border-gray-200 pt-6">
        <div className="flex items-center gap-2 mb-4">
          <PenLine className="h-5 w-5 text-brand-600" />
          <h3 className="text-base font-semibold text-gray-800 m-0">Assinaturas do Plano</h3>
          {saveStatus === 'saving' && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400 ml-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Salvando...
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 ml-2">
              <Check className="h-3 w-3" />
              Salvo
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="inline-flex items-center gap-1 text-xs text-red-500 ml-2">
              <AlertCircle className="h-3 w-3" />
              Erro ao salvar
            </span>
          )}
        </div>

        <div className="space-y-3">
          {signoffs.map((entry) => (
            <div
              key={entry.id}
              className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-200"
            >
              {/* Role select */}
              <select
                value={entry.role}
                onChange={(e) => handleChange(entry.id, 'role', e.target.value)}
                disabled={readOnly}
                className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>

              {/* Specialty (only for Terapeuta) */}
              {entry.role === 'Terapeuta' && (
                <select
                  value={entry.specialty || ''}
                  onChange={(e) => handleChange(entry.id, 'specialty', e.target.value)}
                  disabled={readOnly}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">Especialidade...</option>
                  {SPECIALTY_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              )}

              {/* Name input */}
              <input
                type="text"
                value={entry.name || ''}
                onChange={(e) => handleChange(entry.id, 'name', e.target.value)}
                disabled={readOnly}
                placeholder="Nome"
                className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 placeholder-gray-400"
              />

              {/* Date input */}
              <input
                type="date"
                value={entry.date || ''}
                onChange={(e) => handleChange(entry.id, 'date', e.target.value)}
                disabled={readOnly}
                className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />

              {/* Remove button */}
              {!readOnly && (
                <button
                  onClick={() => handleRemove(entry.id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                  title="Remover"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {!readOnly && (
          <button
            onClick={handleAdd}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-700 hover:text-brand-800 bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Adicionar Assinatura
          </button>
        )}
      </div>

      {/* Print version */}
      <div className="signoff-section-print hidden print:block mt-10 pt-6" style={{ borderTop: '2px solid #000' }}>
        <h2 style={{ fontSize: '14pt', fontWeight: 'bold', marginBottom: '24pt', textAlign: 'center' }}>
          ASSINATURAS
        </h2>

        {signoffs.map((entry) => {
          const roleLabel = entry.role === 'Terapeuta' && entry.specialty
            ? entry.specialty
            : entry.role;
          const formattedDate = entry.date
            ? new Date(entry.date + 'T12:00:00').toLocaleDateString('pt-BR')
            : '____/____/________';

          return (
            <div key={entry.id} className="signoff-print-entry" style={{ marginBottom: '28pt', pageBreakInside: 'avoid' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div style={{ borderBottom: '1px solid #000', width: '60%', minHeight: '1px' }}></div>
                <span style={{ fontSize: '10pt' }}>Data: {formattedDate}</span>
              </div>
              <div style={{ marginTop: '4pt', fontSize: '10pt' }}>
                {entry.name && <div>Nome: {entry.name}</div>}
                <div>Cargo: {roleLabel}</div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default TreatmentPlanSignoff;
